import { type IGiftRegistryAdminRefundItem } from '@mahawi/eshop-common/src/types';
import { Button, Drawer, Popconfirm, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../../fragments/date-format';
import { EMPTY_VALUE } from '../../fragments/empty-value';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import { giftRegistryRefundContributionMarkRefunded } from '../../reducers/gift-registry/reducer';
import CopyToClipboard from '../copy-to-clipboard';

const { Text } = Typography;

type TContributionRow = IGiftRegistryAdminRefundItem['contributions'][number];

function GiftRegistryRefundContributionsDrawer({
  open,
  onClose,
  item,
}: {
  open: boolean;
  onClose: () => void;
  item: IGiftRegistryAdminRefundItem | null;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const inProcess = useSelector((s: RootState) => s.GiftRegistry.inProcess);

  return (
    <Drawer
      title={
        item
          ? `Contributions awaiting refund — ${item.giftRegistry.title}`
          : 'Contributions awaiting refund'
      }
      open={open}
      onClose={onClose}
      size={520}
    >
      <Table<TContributionRow>
        rowKey="uuid"
        loading={inProcess}
        dataSource={item?.contributions ?? []}
        pagination={false}
        columns={[
          {
            title: 'Donor',
            dataIndex: 'donorName',
            render: (
              donorName: string,
              record: TContributionRow,
            ): React.ReactElement => (
              <>
                <Text>{donorName}</Text>
                {record.donorEmail && (
                  <>
                    <br />
                    <Text type="secondary">{record.donorEmail}</Text>
                  </>
                )}
              </>
            ),
          },
          {
            title: 'Amount',
            dataIndex: 'amount',
            render: (amount: number): string =>
              `${amount} ${item?.currencyType.isoCode ?? ''}`,
          },
          {
            title: 'Comgate transaction',
            dataIndex: 'thirdPartyPaymentId',
            render: (thirdPartyPaymentId: string | null): React.ReactElement =>
              thirdPartyPaymentId ? (
                <CopyToClipboard text={thirdPartyPaymentId} size="small">
                  <Text code>{thirdPartyPaymentId}</Text>
                </CopyToClipboard>
              ) : (
                <Text type="secondary">{EMPTY_VALUE}</Text>
              ),
          },
          {
            title: 'Captured at',
            dataIndex: 'capturedAt',
            render: (capturedAt: Date | null): string =>
              capturedAt
                ? dayjs(capturedAt).format(EDateFormat.DATE_TIME_SHORT)
                : EMPTY_VALUE,
          },
          {
            title: 'Actions',
            render: (
              _text: unknown,
              record: TContributionRow,
            ): React.ReactElement => (
              <Popconfirm
                title="Mark this contribution as refunded?"
                description="Only do this after refunding the donor manually in the Comgate dashboard."
                onConfirm={(): void => {
                  dispatch(
                    giftRegistryRefundContributionMarkRefunded(record.uuid),
                  );
                }}
              >
                <Button size="small" disabled={inProcess}>
                  Mark as refunded
                </Button>
              </Popconfirm>
            ),
          },
        ]}
      />
    </Drawer>
  );
}

export default GiftRegistryRefundContributionsDrawer;
